import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, LayoutDashboard, LogOut, Package, Settings, ShoppingBag, Tag, Users } from "lucide-react";
import { adminApi, fmt } from "../../lib/shopApi";
import { ProductsTab } from "../../components/admin/ProductsTab";
import { OrdersTab } from "../../components/admin/OrdersTab";
import { DiscountsTab } from "../../components/admin/DiscountsTab";
import { SettingsTab } from "../../components/admin/SettingsTab";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "products", label: "Products", icon: Package },
  { key: "orders", label: "Orders", icon: ShoppingBag },
  { key: "customers", label: "Customers", icon: Users },
  { key: "discounts", label: "Discounts", icon: Tag },
  { key: "settings", label: "Settings", icon: Settings },
];

const StatCard = ({ label, value, testid }) => (
  <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-5">
    <p className="text-[#F5EFE3]/45 text-xs uppercase tracking-widest mb-2">{label}</p>
    <p className="font-serif text-2xl md:text-3xl text-[#C9A24B]" data-testid={testid}>{value}</p>
  </div>
);

const OverviewTab = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminApi.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <p className="text-[#F5EFE3]/40 text-sm py-10 text-center">Loading overview…</p>;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Sales" value={fmt(stats.total_sales_cents)} testid="stat-total-sales" />
        <StatCard label="Orders This Week" value={stats.orders_week} testid="stat-orders-week" />
        <StatCard label="Orders This Month" value={stats.orders_month} testid="stat-orders-month" />
        <StatCard label="Open Orders" value={stats.pending_orders} testid="stat-pending-orders" />
      </div>

      {stats.low_stock.length > 0 && (
        <div className="bg-red-500/10 border border-red-400/30 rounded-2xl p-5" data-testid="low-stock-alert">
          <p className="text-red-300 font-semibold text-sm mb-3">Low stock alert</p>
          <div className="space-y-1.5">
            {stats.low_stock.map((p) => (
              <p key={p.id} className="text-[#F5EFE3]/70 text-sm">
                {p.name} — <span className="text-red-300">{p.stock} left</span>
              </p>
            ))}
          </div>
          <button onClick={() => onNavigate("products")} className="mt-4 text-[#C9A24B] text-xs uppercase tracking-widest underline underline-offset-4">
            Restock in Products
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-5">
          <p className="text-[#C9A24B] text-xs uppercase tracking-widest mb-4">Best Sellers</p>
          {stats.best_sellers.length === 0 ? (
            <p className="text-[#F5EFE3]/40 text-sm">No sales yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.best_sellers.map((b, i) => (
                <div key={b.name} className="flex items-center justify-between text-sm">
                  <span className="text-[#F5EFE3]/75"><span className="text-[#C9A24B] font-serif mr-2">{i + 1}.</span>{b.name}</span>
                  <span className="text-[#F5EFE3]/50">{b.qty} sold · {fmt(b.revenue_cents)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-5">
          <p className="text-[#C9A24B] text-xs uppercase tracking-widest mb-4">Recent Orders</p>
          {stats.recent_orders.length === 0 ? (
            <p className="text-[#F5EFE3]/40 text-sm">No orders yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between text-sm">
                  <span className="text-[#F5EFE3]/75">{o.order_number} · {o.customer.name}</span>
                  <span className="text-[#C9A24B]">{fmt(o.total_cents)}</span>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => onNavigate("orders")} className="mt-4 text-[#C9A24B] text-xs uppercase tracking-widest underline underline-offset-4">
            View all orders
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomersTab = () => {
  const [customers, setCustomers] = useState(null);

  useEffect(() => {
    adminApi.get("/customers").then((r) => setCustomers(r.data.customers)).catch(() => setCustomers([]));
  }, []);

  if (!customers) return <p className="text-[#F5EFE3]/40 text-sm py-10 text-center">Loading customers…</p>;
  if (customers.length === 0) return <p className="text-[#F5EFE3]/40 text-sm py-10 text-center" data-testid="customers-empty">No customers yet — they'll appear here after their first order.</p>;

  return (
    <div className="space-y-3" data-testid="customers-list">
      {customers.map((c) => (
        <div key={c.id} className="bg-[#232323] border border-[#C9A24B]/10 rounded-2xl p-5 flex flex-wrap justify-between gap-3">
          <div>
            <p className="font-semibold">{c.name}</p>
            <p className="text-[#F5EFE3]/50 text-sm">{c.email} · {c.phone}</p>
          </div>
          <div className="text-right">
            <p className="text-[#C9A24B] font-semibold">{fmt(c.total_spent_cents)}</p>
            <p className="text-[#F5EFE3]/45 text-xs">{c.order_count} order{c.order_count === 1 ? "" : "s"}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    document.title = "Admin | Loctician Carlocs";
    const token = localStorage.getItem("cc_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    adminApi
      .get("/stats")
      .then(() => {
        setUser({ email: localStorage.getItem("cc_admin_email") || "Admin" });
        setChecking(false);
      })
      .catch((e) => {
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          localStorage.removeItem("cc_admin_token");
          navigate("/admin/login");
        } else {
          setChecking(false);
        }
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("cc_admin_token");
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#1C1C1C] text-[#F5EFE3]/40 flex items-center justify-center text-sm">Checking access…</div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1C] text-[#F5EFE3]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#1C1C1C]/90 backdrop-blur-xl border-b border-[#C9A24B]/15">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Crown className="w-5 h-5 text-[#C9A24B]" />
            <span className="font-serif text-lg">Carlocs <span className="italic text-[#C9A24B]">Admin</span></span>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-[#F5EFE3]/50 hover:text-[#C9A24B] text-xs uppercase tracking-widest transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-3 flex gap-2 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-widest whitespace-nowrap transition-colors duration-300 ${
                tab === t.key ? "bg-[#C9A24B] text-[#1C1C1C] font-bold" : "text-[#F5EFE3]/55 hover:text-[#C9A24B] border border-[#C9A24B]/15"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {tab === "overview" && <OverviewTab onNavigate={setTab} />}
        {tab === "products" && <ProductsTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "discounts" && <DiscountsTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
};

export default AdminDashboard;
